import { PITCH_XI_ROWS, PITCH_BENCH_ROW } from './player-row';
import PitchShell from './pitch-shell';
import PitchPlayer, { PlayerInfo } from './pitch-player';
import { PlayerCircle } from './player-circle';
import { buildXiRowsByPosition, XI_SLOT_MAX } from './team-helpers';
import type { PitchFlagType } from '@components/quiz/ReadinessProvider/utils/pitch-flags';

/**
 * The minimal player shape the pitch renders. Kept independent of the
 * builder's full Player and the funnel's QuizPlayer so any caller can feed it
 * — names and silhouettes show immediately, portraits fill in once resolved.
 */
export type SquadPitchPlayer = {
  id: string;
  displayName: string;
  teamShortName: string;
  price: number | null;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  imageUrl: string | null;
};

export type SquadPitchMember = {
  playerId: number;
  player: SquadPitchPlayer | null;
};

const FLAG_STYLES: Record<PitchFlagType, { label: string; className: string }> =
  {
    aiCaptain: {
      label: "AI'S CAPTAIN",
      className: 'bg-warning-500 text-white'
    },
    captainMatch: {
      label: 'CAPTAIN ✓',
      className: 'bg-warning-500 text-neutral-900'
    },
    yourCaptain: {
      label: 'YOUR CAPTAIN',
      className: 'bg-warning-500 text-neutral-900'
    },
    yourSell: {
      label: 'YOUR SELL',
      className: 'bg-danger-500 text-neutral-900'
    },
    aiSell: { label: "AI'S SELL", className: 'bg-danger-500 text-white' }
  };

const FlagChip = ({ flag }: { flag: PitchFlagType }) => (
  <span
    className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[8px] font-black tracking-wider shadow ${FLAG_STYLES[flag].className}`}
  >
    {FLAG_STYLES[flag].label}
  </span>
);

type SquadPitchProps = {
  /** 15 slots, XI-first (0-10) then bench (11-14). */
  team: SquadPitchMember[];
  /** Player id (UUID) of the captain, to show the "C" armband. */
  captainId?: string | null;
  /** Tap a starter (captain / transfer-out selection); receives the UUID. */
  onPlayerTap?: (playerId: string) => void;
  /** UUID of the currently selected player (highlight ring). */
  selectedPlayerId?: string | null;
  /** UUID of a player to render dimmed (e.g. the selected transfer-out). */
  dimmedPlayerId?: string | null;
  /** Disable all taps. */
  isTapDisabled?: boolean;
  /** Verdict flags keyed by player UUID — renders a chip above the player. */
  flagsByPlayerId?: Map<string, PitchFlagType>;
};

const EmptySlot = () => (
  <div className="flex justify-center z-10">
    <div className="flex flex-col items-center">
      <PlayerCircle />
      <PlayerInfo invisible />
    </div>
  </div>
);

const SquadRow = ({
  members,
  className,
  captainId,
  onPlayerTap,
  selectedPlayerId,
  dimmedPlayerId,
  isTapDisabled,
  flagsByPlayerId
}: { members: SquadPitchMember[]; className: string } & Omit<
  SquadPitchProps,
  'team'
>) => (
  <div className={`flex items-end ${className}`}>
    {members.map(({ playerId, player }) => {
      if (!player) return <EmptySlot key={`sq-${playerId}`} />;

      const flag = flagsByPlayerId?.get(player.id);
      const token = (
        <PitchPlayer
          name={player.displayName}
          teamCode={player.teamShortName}
          price={
            player.price != null ? `£${player.price.toFixed(1)}m` : undefined
          }
          imageUrl={player.imageUrl}
          isCaptain={Boolean(captainId) && player.id === captainId}
          isSelected={selectedPlayerId === player.id}
          topBadge={flag ? <FlagChip flag={flag} /> : undefined}
        />
      );

      const isGoalkeeper = player.position === 'GK';
      // Only the starting XI (slots 0–10) is selectable; bench slots
      // (11–14) are display-only, matching the pre-restyle pitch.
      const isStarter = playerId <= XI_SLOT_MAX;
      const isInteractive =
        Boolean(onPlayerTap) && !isTapDisabled && !isGoalkeeper && isStarter;
      const isDimmed = dimmedPlayerId === player.id;

      if (!isInteractive) {
        return (
          <div
            key={`sq-${playerId}`}
            className={isDimmed ? 'opacity-40' : undefined}
          >
            {token}
          </div>
        );
      }

      return (
        <button
          key={`sq-${playerId}`}
          type="button"
          onClick={() => onPlayerTap?.(player.id)}
          className={`transition-all duration-300 active:scale-95 ${
            isDimmed ? 'opacity-40' : ''
          }`}
        >
          {token}
        </button>
      );
    })}
  </div>
);

const membersInRange = (team: SquadPitchMember[], range: [number, number]) =>
  team.filter(({ playerId }) => playerId >= range[0] && playerId <= range[1]);

/**
 * The GYTR portrait pitch (PitchShell + PitchPlayer) adapted for the
 * readiness flow: optionally interactive (tap a starter to pick captain or
 * the transfer-out) and optionally flagged (verdict chips above players).
 * Keeps the builder/reveal pitch styling so every funnel pitch matches.
 *
 * The rated squad can come back in any legal formation, so the starting XI is
 * grouped by each player's actual position rather than the builder's fixed
 * rows; the fixed rows are the fallback when the XI isn't fully resolved.
 */
const SquadPitch = ({ team, ...rest }: SquadPitchProps) => {
  const positionRows = buildXiRowsByPosition(team);
  const xiRows =
    positionRows ??
    PITCH_XI_ROWS.map((row) => ({
      key: `pos-${row.positionId}`,
      members: membersInRange(team, row.range),
      className: row.className
    }));

  return (
    <PitchShell
      xi={xiRows.map((row) => (
        <SquadRow
          key={`sq-row-${row.key}`}
          members={row.members}
          className={row.className}
          {...rest}
        />
      ))}
      bench={
        <SquadRow
          members={membersInRange(team, PITCH_BENCH_ROW.range)}
          className={PITCH_BENCH_ROW.className}
          {...rest}
        />
      }
    />
  );
};

export default SquadPitch;
